import type { Router } from "vue-router";
import { useFabriqStore } from "@/store/fabriq";
import { useTicketsStore } from "@/store/tickets";
import { useFieldsStore } from "@/store/fields";
import { useTasksStore } from "@/store/tasks";
import { useTeamsStore } from "@/store/teams";
import { useCommentsStore } from "@/store/comments";
import { useRoutinesStore } from "@/store/routines";
import { useInstancesStore } from "@/store/instances";
import { useSchedulesStore } from "@/store/schedules";
import { useTemplatesStore } from "@/store/templates";
import { useExecutionsStore } from "@/store/executions";
import { useAnswersStore } from "@/store/answers";
import { useFilesStore } from "@/store/files";
import { useIndicatorsStore } from "@/store/indicators";
import { useDependenciesStore } from "@/store/dependencies";
import { useDatapointsStore } from "@/store/datapoints";
import { useNotificationsStore } from "@/store/notifications";
import { useTicketTemplatesStore } from "@/store/ticketTemplates";
import storage from "./storage";
import { useClientStore } from "@/store/client";
import { parseISO } from "date-fns";
import { useEventTypesStore } from "@/store/eventTypes";
import { useEventsStore } from "@/store/events";
import { useDiamondDependenciesStore } from "@/store/diamondDependencies";
import { reload } from "./live-updates";
import { localLogger } from "./localLogger";

export const initializeApp = async (router: Router, firstLoad = false) => {
  await localLogger.log(`initializeApp START`);
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const fabriqStore = useFabriqStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const ticketsStore = useTicketsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const tasksStore = useTasksStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const teamsStore = useTeamsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const commentsStore = useCommentsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const routinesStore = useRoutinesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const instancesStore = useInstancesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const schedulesStore = useSchedulesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const templatesStore = useTemplatesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const executionsStore = useExecutionsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const answersStore = useAnswersStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const filesStore = useFilesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const indicatorsStore = useIndicatorsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const dependenciesStore = useDependenciesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const datapointsStore = useDatapointsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const notificationsStore = useNotificationsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const ticketTemplatesStore = useTicketTemplatesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const eventTypesStore = useEventTypesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const eventsStore = useEventsStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const diamondDependenciesStore = useDiamondDependenciesStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const fields = useFieldsStore();
  fabriqStore.setLoading(true);
  localStorage.setItem("last-tickets-open", "0");
  localStorage.setItem("last-notifications-open", "0");
  await Promise.all([
    ticketsStore.initialize(),
    fields.initialize(),
    tasksStore.initialize(),
    teamsStore.initialize(),
    commentsStore.initialize(),
    routinesStore.initialize(),
    instancesStore.initialize(),
    schedulesStore.initialize(),
    filesStore.initialize(),
    dependenciesStore.initialize(),
    templatesStore.initialize(),
    executionsStore.initialize(),
    answersStore.initialize(),
    indicatorsStore.initialize(),
    datapointsStore.initialize(),
    notificationsStore.initialize(),
    ticketTemplatesStore.initialize(),
    eventTypesStore.initialize(),
    eventsStore.initialize(),
    diamondDependenciesStore.initialize(),
  ]);
  await localLogger.log(`initializeApp load user data`);

  const promises = [
    fabriqStore.loadUserData(teamsStore),
    ticketTemplatesStore.all(),
    eventTypesStore.all(),
    notificationsStore.getCount(),
  ];
  notificationsStore.all({ flush: true, all: false });
  if (firstLoad) await Promise.allSettled(promises);
  await localLogger.log(`initializeApp connectSocket`);
  await fabriqStore.connectSocket();
  if (localStorage.getItem("updateRequired") === "true") {
    await reload();
  }
  await localLogger.log(`initializeApp END`);
  await router.replace("/main/tickets");
};

export const autoLogin = async (router: any) => {
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const fabriqStore = useFabriqStore();
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const clientStore = useClientStore();
  let email, password;
  await localLogger.log(`autoLogin START`);
  try {
    email = await storage.get("email");
    password = await storage.get("password");
  } catch (e) {
    console.error("ARG", e);
  }

  const user = await storage.readFromStorage("user");
  const client = await storage.readFromStorage("client");

  const token = await storage.get("token");
  const connected = await storage.get("connected");
  if (client && token && user && connected) {
    fabriqStore.setInitializationState({ type: "pending" });
    await fabriqStore.setCredentials(email, password);
    fabriqStore.setUser(user);
    clientStore.setClient(client);
    fabriqStore.setToken({
      ...token,
      expiresAt: parseISO(token.expiresAt),
    });
    await localLogger.log(`autoLogin END 1`);
    return initializeApp(router, false);
  } else {
    const response = await fabriqStore.checkCurrent();

    if (response && response.token && response.user) {
      fabriqStore.setInitializationState({ type: "pending" });
      await fabriqStore.setCredentials(email, password);
      fabriqStore.setToken(response.token);
      await localLogger.log(`autoLogin END 2`);
      return initializeApp(router, true);
    }
  }
  await localLogger.log(`autoLogin END 3`);
};
